<template>
  <div class="patient-view">
    <div v-if="showAudioPrompt" class="audio-prompt">{{ $t('patient.audioPrompt') }}</div>
    <BallStage
      :speed="speed"
      :bounce-mode="bounceMode"
      :is-moving="isMoving"
      :ball-size="ballSize"
      :background-color="backgroundColor"
      :background-image="backgroundImage"
      :background-fit="backgroundFit"
      :ball-color="ballColor"
      :random-color="randomColor"
      @turn="player.syncTurn($event)"
      @tempo="player.setTempo($event)"
      @click.native="unlockAudio"
    />
  </div>
</template>

<script>
import axios from 'axios';
import BallStage from './components/BallStage.vue';
import { applySeo } from './i18n/head';
import { BilateralPlayer, DEFAULT_SOUND } from './sounds';
import { presetUrl } from './backgrounds';

export default {
  components: { BallStage },
  data() {
    return {
      speed: 5,
      bounceMode: 'horizontal',
      ballSize: 30, // px
      backgroundColor: '#ffffff',
      backgroundImage: '',
      backgroundFit: 'cover',
      ballColor: '#2196f3',
      randomColor: false,
      isMoving: false,
      bilateralSoundActive: false,
      audioUnlocked: false,
      showAudioPrompt: true,
      fetchInterval: null,
      sessionId: ''
    };
  },
  created() {
    // Kept out of data() so Vue doesn't make the AudioContext reactive
    this.player = new BilateralPlayer();
  },
  mounted() {
    applySeo({
      locale: this.$locale,
      routeBase: 'patient',
      title: this.$t('patient.meta.title'),
      description: this.$t('patient.meta.description'),
      noindex: true
    });
    this.getSessionIdFromUrl();

    // Start polling for state updates pushed by the therapist
    this.fetchBall();
    this.fetchInterval = setInterval(this.fetchBall, 500);
  },
  beforeDestroy() {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
    }
    this.player.close();
  },
  methods: {
    unlockAudio() {
      if (!this.audioUnlocked) {
        this.audioUnlocked = true;
        this.showAudioPrompt = false;
        try {
          this.player.unlock();
          if (this.bilateralSoundActive) {
            this.player.start();
          }
        } catch (e) {
          // Audio initialization failed
        }
      }
    },
    async fetchBall() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';

      if (!this.sessionId) {
        console.error('Patient: No session ID!');
        return;
      }

      try {
        // Fetch all state in parallel
        const [ballRes, bgRes, colorRes, soundRes] = await Promise.all([
          axios.get(`/api/ball${sessionParam}`),
          axios.get(`/api/background${sessionParam}`),
          axios.get(`/api/ballcolor${sessionParam}`),
          axios.get(`/api/sound${sessionParam}`)
        ]);

        // Update ball state
        this.speed = ballRes.data.speed;
        this.bounceMode = ballRes.data.bounceMode || 'horizontal';
        this.isMoving = ballRes.data.isMoving || false;
        if (ballRes.data.ballSize !== undefined) {
          this.ballSize = ballRes.data.ballSize;
        }

        // Update background
        this.backgroundColor = bgRes.data.backgroundColor || '#ffffff';
        this.applyBackgroundImage(bgRes.data.image);

        // Update ball color
        this.randomColor = colorRes.data.randomColor || false;
        this.ballColor = colorRes.data.ballColor || '#2196f3';

        // Update bilateral sound
        this.player.setSound(soundRes.data.sound || DEFAULT_SOUND);
        this.bilateralSoundActive = soundRes.data.bilateral === true;
        if (this.bilateralSoundActive) {
          this.player.start();
        } else {
          this.player.stop();
        }
      } catch (error) {
        console.error('Error fetching state:', error);
      }
    },
    applyBackgroundImage(image) {
      if (!image) {
        this.backgroundImage = '';
        return;
      }
      // Uploaded images are streamed from server memory with no-store, so the
      // patient's browser keeps them only in memory while the page is open.
      this.backgroundImage = image.kind === 'upload'
        ? `/api/background/image?session=${encodeURIComponent(this.sessionId)}&v=${image.id}`
        : presetUrl(image.id);
      this.backgroundFit = image.fit || 'cover';
    },
    getSessionIdFromUrl() {
      const params = new URLSearchParams(window.location.search);
      this.sessionId = params.get('session') || '';
    }
  }
};
</script>

<style scoped>
/* Audio unlock prompt styling */
.audio-prompt {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--c-primary, #2B6960);
  color: #fff;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-family: var(--ff-body, 'Inter', -apple-system, sans-serif);
  font-size: 1rem;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 8px 32px rgba(43, 105, 96, 0.3);
  letter-spacing: 0.02em;
  pointer-events: none;
}
@media (max-width: 600px) {
  .audio-prompt {
    padding: 1rem 2rem;
    font-size: 0.92rem;
    max-width: 85vw;
    text-align: center;
  }
}
.patient-view {
  position: relative;
  height: 100vh;
  width: 100vw;
  margin: 0;
  padding: 0;
  overflow: hidden;
}
</style>
